import { useAppDispatch, useAppSelector } from 'store/hooks';
import { PermissionTypes } from 'features/permissions/types';
import { usePermissions } from 'features/permissions/usePermissions';
import { selectLocalMuteEnabled } from 'features/room/roomSlice';
import { selectLocalUserId } from 'features/users/usersSlice';
import { User } from 'features/users/types';
import { isolateAudioRequested } from 'features/local-mute/actions';
import {
  hearEveryoneRequested,
  selectIsAnyLocalMuteActive,
  selectIsUserLocallyMuted,
  userMutedLocally,
  userUnmutedLocally,
} from 'features/local-mute/localMuteSlice';

export const useLocalMute = (user?: User) => {
  const dispatch = useAppDispatch();

  const enabled = useAppSelector(selectLocalMuteEnabled);
  const localUserId = useAppSelector(selectLocalUserId);
  const isMuted = useAppSelector((state) => selectIsUserLocallyMuted(state, user?.id ?? ''));
  const isAnyMuteActive = useAppSelector(selectIsAnyLocalMuteActive);

  const { hasPermissions } = usePermissions();

  const targetCanBroadcastAudio =
    !!user && hasPermissions(PermissionTypes.broadcastAudio, { targetUser: user });

  const isRemoteTarget = enabled && !!user && user.id !== localUserId;

  // An existing mute must stay clearable even after the target loses broadcastAudio; new mutes still require it.
  const available = isRemoteTarget && (targetCanBroadcastAudio || isMuted);

  // Isolating a permission-less muted user would silence everyone, so no muted-exception here.
  const canIsolate = isRemoteTarget && targetCanBroadcastAudio;

  const toggleMute = () => {
    if (!user) {
      return;
    }
    dispatch(isMuted ? userUnmutedLocally(user.id) : userMutedLocally(user.id));
  };

  const isolate = () => {
    if (!user) {
      return;
    }
    dispatch(isolateAudioRequested(user.id));
  };

  const hearEveryone = () => {
    dispatch(hearEveryoneRequested());
  };

  return { available, canIsolate, isMuted, isAnyMuteActive, toggleMute, isolate, hearEveryone };
};
